import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Plus,
  Search,
  Clock,
  MoreVertical,
  Trash2,
  Edit,
  Sparkles,
  Loader2,
  ArrowRight,
  ImageIcon,
  Wand2,
  Palette,
  Zap,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Quick start templates shown on the main page
const FEATURED_TEMPLATES = [
  {
    id: "text-to-image",
    name: "Text to Image",
    description: "Generate images from text descriptions",
    icon: ImageIcon,
    color: "bg-indigo-500",
  },
  {
    id: "image-to-image",
    name: "Transform Image",
    description: "Modify and enhance existing images",
    icon: Wand2,
    color: "bg-purple-500",
  },
  {
    id: "style-transfer",
    name: "Style Match",
    description: "Apply styles from reference images",
    icon: Palette,
    color: "bg-pink-500",
  },
  {
    id: "architectural-render",
    name: "Architectural Render",
    description: "Enhance architectural visualizations",
    icon: Sparkles,
    color: "bg-amber-500",
  },
];

export default function WorkflowsListSimple() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [newName, setNewName] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    try {
      const res = await axios.get(`${API}/ai-workflows/boards`, { headers });
      setWorkflows(res.data.workflows || []);
    } catch (error) {
      console.error("Failed to load workflows:", error);
      toast.error("Failed to load workflows");
    } finally {
      setLoading(false);
    }
  };

  const createFromTemplate = (templateId) => {
    navigate(`/ai-workflows/new?template=${templateId}`);
  };

  const createBlankWorkflow = async () => {
    try {
      const res = await axios.post(
        `${API}/ai-workflows/boards`,
        { name: "Untitled Workflow", description: "" },
        { headers }
      );
      navigate(`/ai-workflows/${res.data.id}`);
    } catch (error) {
      console.error("Failed to create workflow:", error);
      toast.error("Failed to create workflow");
    }
  };

  const deleteWorkflow = async (id, e) => {
    e?.stopPropagation();
    if (!confirm("Delete this workflow?")) return;
    
    try {
      await axios.delete(`${API}/ai-workflows/boards/${id}`, { headers });
      toast.success("Workflow deleted");
      loadWorkflows();
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error("Failed to delete workflow");
    }
  };

  const renameWorkflow = async () => {
    if (!renameTarget || !newName.trim()) return;
    
    try {
      await axios.patch(
        `${API}/ai-workflows/boards/${renameTarget.id}`,
        { name: newName },
        { headers }
      );
      toast.success("Workflow renamed");
      loadWorkflows();
      setShowRenameDialog(false);
      setRenameTarget(null);
      setNewName("");
    } catch (error) {
      console.error("Failed to rename:", error);
      toast.error("Failed to rename workflow");
    }
  };

  const filteredWorkflows = workflows.filter(w =>
    w.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="workflows-list-simple">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-light text-slate-900 dark:text-white mb-2">
            AI Workflow Board
          </h1>
          <p className="text-lg text-slate-500 dark:text-slate-400">
            Create and run AI-powered creative workflows
          </p>
        </div>

        {/* Quick Start Templates */}
        <section className="mb-12">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-4">
            Quick Start
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURED_TEMPLATES.map((template) => (
              <button
                key={template.id}
                onClick={() => createFromTemplate(template.id)}
                className="group p-5 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 hover:border-indigo-500 dark:hover:border-indigo-500 transition-all text-left hover:shadow-lg"
                data-testid={`quick-start-${template.id}`}
              >
                <div className={`inline-flex p-3 ${template.color} rounded-xl mb-4`}>
                  <template.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                  {template.name}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {template.description}
                </p>
              </button>
            ))}
          </div>
        </section>

        {/* Recent Workflows */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Your Workflows
            </h2>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 w-64 bg-white dark:bg-slate-900"
                />
              </div>
              <Button
                onClick={createBlankWorkflow}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="create-workflow-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Workflow
              </Button>
            </div>
          </div>

          {filteredWorkflows.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-full mb-4">
                  <Zap className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                  No workflows yet
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-center mb-4 max-w-sm">
                  Get started by choosing a template above or create a blank workflow
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredWorkflows.map((workflow) => (
                <div
                  key={workflow.id}
                  onClick={() => navigate(`/ai-workflows/${workflow.id}`)}
                  className="group flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-indigo-500 dark:hover:border-indigo-500 cursor-pointer transition-all"
                  data-testid={`workflow-${workflow.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2.5 bg-indigo-100 dark:bg-indigo-500/20 rounded-xl">
                      <Sparkles className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                        {workflow.name}
                      </h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(workflow.updated_at || workflow.created_at)}
                        </span>
                        <StatusBadge status={workflow.status} />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          setRenameTarget(workflow);
                          setNewName(workflow.name);
                          setShowRenameDialog(true);
                        }}>
                          <Edit className="w-4 h-4 mr-2" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={(e) => deleteWorkflow(workflow.id, e)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-500 transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Rename Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Workflow</DialogTitle>
          </DialogHeader>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Workflow name"
            onKeyDown={(e) => e.key === "Enter" && renameWorkflow()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenameDialog(false)}>
              Cancel
            </Button>
            <Button onClick={renameWorkflow}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatDate(dateString) {
  if (!dateString) return "Unknown";
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return "Just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return date.toLocaleDateString();
}

function StatusBadge({ status }) {
  if (!status || status === "draft") {
    return (
      <Badge variant="outline" className="text-xs bg-slate-100 dark:bg-slate-800 text-slate-500">
        Draft
      </Badge>
    );
  }
  if (status === "active") {
    return (
      <Badge className="text-xs bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400">
        Active
      </Badge>
    );
  }
  return null;
}
